// Shared helper for surfacing API error messages to users.
//
// React Query mutation onError handlers receive an `unknown` (typically
// an axios error wrapping the JSON envelope produced by the server's
// errorHandler middleware: { error, code, message, fields? }). Pages
// were previously logging to the console and either dropping the user
// at a silent failure or showing a stale generic banner.
//
// Use:
//   onError: (err) => setError(extractErrorMessage(err))
//
// extractErrorMessage prefers the server's structured `message` field
// (the operator-friendly text the service deliberately emits — e.g.
// "Cannot mark delivered — no skip unit is assigned"), falls back to
// the axios error message, and finally to a sensible default. The
// status-aware variant adds canned copy for the well-known HTTP codes
// the spec calls out.

export function extractErrorMessage(error: unknown): string {
  if (error && typeof error === 'object') {
    const e = error as {
      response?: {
        status?: number;
        data?: {
          message?: string;
          error?: string;
          fields?: Record<string, string>;
        };
      };
      message?: string;
    };

    // Axios error path: prefer the server's structured message.
    const serverMsg = e.response?.data?.message ?? e.response?.data?.error;
    if (typeof serverMsg === 'string' && serverMsg.length > 0) {
      return serverMsg;
    }

    // Field-level Zod errors — surface the first one.
    const fields = e.response?.data?.fields;
    if (fields && typeof fields === 'object') {
      const first = Object.entries(fields)[0];
      if (first !== undefined) {
        const [path, message] = first;
        return path === '_' ? message : `${path}: ${message}`;
      }
    }

    if (typeof e.message === 'string' && e.message.length > 0) {
      return e.message;
    }
  }

  if (error instanceof Error) return error.message;
  return 'An unexpected error occurred.';
}

/**
 * Status-aware variant — applies the canned copy from the punch-list
 * spec when the server response carries no specific message. The
 * server's structured message (when present) always wins.
 */
export function extractErrorMessageWithStatus(error: unknown): string {
  if (error && typeof error === 'object') {
    const e = error as {
      response?: {
        status?: number;
        data?: { message?: string; error?: string };
      };
    };
    const status = e.response?.status;
    const serverMsg = e.response?.data?.message ?? e.response?.data?.error;
    if (typeof serverMsg === 'string' && serverMsg.length > 0) {
      return serverMsg;
    }
    if (typeof status === 'number') {
      if (status === 409) {
        return 'A conflict occurred — please check your selections and try again.';
      }
      if (status === 500) {
        return 'A server error occurred. Please try again or contact support.';
      }
    }
  }
  return extractErrorMessage(error);
}
